import { Component, OnInit } from '@angular/core'
import { Parking, ParkingStatus, Term } from '@parkupp/core'
import { ParkingService } from 'src/app/services/parking.service'
import { firstValueFrom } from 'rxjs'
import { MapTypeStyle } from '@agm/core/services/google-maps-types'

const HEAT_MAP_VIEW_STATE_KEY = 'parkupp_admin_heat_map_view'

// Monthly prices under R100 are R1 test/staff listings (same cut-off as Market Insights)
const MIN_MONTHLY_PRICE = 100

// Zones are grid cells roughly this many screen pixels wide, so they shrink as you zoom in
const CELL_SIZE_PX = 56

// Zone needs this many listings to appear in the "Most expensive areas" ranking
const MIN_AREA_LISTINGS = 2

const BAND_COLORS = ['#22c55e', '#a3e635', '#facc15', '#f97316', '#ef4444', '#991b1b']

interface PricedParking {
    parking: Parking
    lat: number
    lng: number
    price: number
}

interface PriceZone {
    id: string
    lat: number
    lng: number
    radius: number
    color: string
    count: number
    median: number
    min: number
    max: number
    parkings: PricedParking[]
}

interface PriceBand {
    color: string
    from: number
    to: number
}

interface AreaRanking {
    name: string
    count: number
    median: number
    lat: number
    lng: number
}

@Component({
    selector: 'app-admin-heat-map',
    templateUrl: './admin-heat-map.component.html',
    styleUrls: ['./admin-heat-map.component.scss'],
})
export class AdminHeatMapComponent implements OnInit {
    cameraLat = -29.320829
    cameraLng = 24.237754
    zoom = 6

    mapStyles: MapTypeStyle[] = [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ]

    loading = true
    pricedParkings: PricedParking[] = []
    zones: PriceZone[] = []
    bands: PriceBand[] = []
    topAreas: AreaRanking[] = []
    selectedZone: PriceZone | null = null
    unpricedCount = 0

    constructor(private parkingService: ParkingService) {
        this.restoreMapView()
    }

    ngOnInit(): void {
        this.loadParkings()
    }

    private async loadParkings(): Promise<void> {
        this.loading = true
        try {
            // Cross-enterprise on purpose: the heat map covers every published listing in South Africa
            const parkings = await firstValueFrom(this.parkingService.listAll({ status: ParkingStatus.PUBLISHED }))
            const live = parkings.filter((p) => !p.deletedAt && p.parkingLocation?.latitude && p.parkingLocation?.longitude)

            this.pricedParkings = []
            for (const parking of live) {
                const price = this.getMonthlyPrice(parking)
                if (price === null) continue
                this.pricedParkings.push({
                    parking,
                    lat: parking.parkingLocation.latitude,
                    lng: parking.parkingLocation.longitude,
                    price,
                })
            }
            this.unpricedCount = live.length - this.pricedParkings.length

            this.bands = this.buildBands(this.pricedParkings.map((p) => p.price))
            this.topAreas = this.buildTopAreas()
            this.buildZones()
        } catch (error) {
            console.error('Error loading heat map:', error)
            this.pricedParkings = []
            this.zones = []
        } finally {
            this.loading = false
        }
    }

    /** Monthly asking price in rands, preferring the MONTHLY plan */
    private getMonthlyPrice(parking: Parking): number | null {
        const plans = parking.plans || []
        const plan = plans.find((p) => p.term === Term.MONTHLY) || plans[0]
        if (!plan?.feeInCents) return null
        const price = plan.feeInCents / 100
        return price >= MIN_MONTHLY_PRICE ? price : null
    }

    /** Six bands split on price quantiles, so a few very expensive listings don't flatten the scale */
    private buildBands(prices: number[]): PriceBand[] {
        if (!prices.length) return []
        const sorted = [...prices].sort((a, b) => a - b)
        const bands: PriceBand[] = []
        for (let i = 0; i < BAND_COLORS.length; i++) {
            const from = i === 0 ? sorted[0] : this.quantile(sorted, i / BAND_COLORS.length)
            const to = i === BAND_COLORS.length - 1 ? sorted[sorted.length - 1] : this.quantile(sorted, (i + 1) / BAND_COLORS.length)
            bands.push({ color: BAND_COLORS[i], from, to })
        }
        return bands
    }

    private colorForPrice(price: number): string {
        for (const band of this.bands) {
            if (price <= band.to) return band.color
        }
        return BAND_COLORS[BAND_COLORS.length - 1]
    }

    /** Group listings into a zoom-dependent grid and colour each cell by its median price */
    private buildZones(): void {
        const cellDegrees = (CELL_SIZE_PX * 360) / (256 * Math.pow(2, this.zoom))
        const cells = new Map<string, PricedParking[]>()

        for (const item of this.pricedParkings) {
            const key = `${Math.floor(item.lat / cellDegrees)}:${Math.floor(item.lng / cellDegrees)}`
            const cell = cells.get(key)
            if (cell) {
                cell.push(item)
            } else {
                cells.set(key, [item])
            }
        }

        const maxCount = Math.max(1, ...Array.from(cells.values()).map((c) => c.length))
        const zones: PriceZone[] = []

        cells.forEach((items, key) => {
            const lat = items.reduce((sum, i) => sum + i.lat, 0) / items.length
            const lng = items.reduce((sum, i) => sum + i.lng, 0) / items.length
            const prices = items.map((i) => i.price).sort((a, b) => a - b)
            const median = this.quantile(prices, 0.5)
            const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, this.zoom)
            const scale = 0.45 + 0.55 * Math.sqrt(items.length / maxCount)

            zones.push({
                id: key,
                lat,
                lng,
                radius: (CELL_SIZE_PX / 2) * metersPerPixel * scale,
                color: this.colorForPrice(median),
                count: items.length,
                median,
                min: prices[0],
                max: prices[prices.length - 1],
                parkings: [...items].sort((a, b) => b.price - a.price),
            })
        })

        // Draw cheaper zones first so expensive ones sit on top where they overlap
        this.zones = zones.sort((a, b) => a.median - b.median)
    }

    private buildTopAreas(): AreaRanking[] {
        const areas = new Map<string, PricedParking[]>()
        for (const item of this.pricedParkings) {
            const loc = item.parking.parkingLocation
            const name = [loc.suburb, loc.city].filter(Boolean).join(', ')
            if (!name) continue
            const area = areas.get(name)
            if (area) {
                area.push(item)
            } else {
                areas.set(name, [item])
            }
        }

        const rankings: AreaRanking[] = []
        areas.forEach((items, name) => {
            if (items.length < MIN_AREA_LISTINGS) return
            rankings.push({
                name,
                count: items.length,
                median: this.quantile(
                    items.map((i) => i.price).sort((a, b) => a - b),
                    0.5
                ),
                lat: items.reduce((sum, i) => sum + i.lat, 0) / items.length,
                lng: items.reduce((sum, i) => sum + i.lng, 0) / items.length,
            })
        })

        return rankings.sort((a, b) => b.median - a.median).slice(0, 15)
    }

    private quantile(sorted: number[], q: number): number {
        if (!sorted.length) return 0
        const pos = (sorted.length - 1) * q
        const base = Math.floor(pos)
        const rest = pos - base
        return sorted[base + 1] !== undefined ? sorted[base] + rest * (sorted[base + 1] - sorted[base]) : sorted[base]
    }

    colorFor(price: number): string {
        return this.colorForPrice(price)
    }

    trackZone(_index: number, zone: PriceZone): string {
        return `${zone.id}:${zone.radius}`
    }

    onZoneClick(zone: PriceZone): void {
        this.selectedZone = zone
    }

    onInfoWindowClose(): void {
        this.selectedZone = null
    }

    focusArea(area: AreaRanking): void {
        this.cameraLat = area.lat
        this.cameraLng = area.lng
        this.zoom = 14
        this.selectedZone = null
        this.buildZones()
        this.persistMapView()
    }

    onCenterChange(center: google.maps.LatLngLiteral): void {
        this.cameraLat = center.lat
        this.cameraLng = center.lng
        this.persistMapView()
    }

    onZoomChange(zoom: number): void {
        if (zoom === this.zoom) return
        this.zoom = zoom
        this.selectedZone = null
        this.buildZones()
        this.persistMapView()
    }

    getMerchantKey(parking: Parking): string | null {
        return parking.merchantRef?.id || null
    }

    private restoreMapView(): void {
        try {
            const saved = localStorage.getItem(HEAT_MAP_VIEW_STATE_KEY)
            if (!saved) return

            const parsed = JSON.parse(saved)
            if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
                this.cameraLat = parsed.lat
                this.cameraLng = parsed.lng
            }
            if (typeof parsed.zoom === 'number') {
                this.zoom = parsed.zoom
            }
        } catch (error) {
            console.warn('Failed to restore heat map view:', error)
        }
    }

    private persistMapView(): void {
        try {
            localStorage.setItem(
                HEAT_MAP_VIEW_STATE_KEY,
                JSON.stringify({
                    lat: this.cameraLat,
                    lng: this.cameraLng,
                    zoom: this.zoom,
                })
            )
        } catch (error) {
            console.warn('Failed to persist heat map view:', error)
        }
    }
}
